/**
 * Notification Hooks for Web Application
 *
 * React hooks for consuming and managing notifications from the
 * session-level notification system integrated with Laravel Echo.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

import {
  NotificationUtils,
  type NotificationItem,
  NOTIFICATION_CONSTANTS,
} from '@personal-trainer-app/shared';
import { registerSessionScoped } from '@personal-trainer-app/shared/src/store/session-scope';
import { formatDisplayDate } from '@personal-trainer-app/shared/src/utils/date';

import type { DateInput } from '@personal-trainer-app/shared/src/utils/date';

import { useEchoContext } from '@/components/providers/EchoProvider';
import { useNotificationActions } from '@/hooks/notifications';
import { useNotificationStore } from '@/stores';

// =============================================================================
// INTERFACES
// =============================================================================

export interface NotificationPreferences {
  soundEnabled: boolean;
  browserNotificationsEnabled: boolean;
  autoMarkAsRead: boolean;
  maxNotifications: number;
}

export interface UseNotificationsOptions {
  /** Enable sound notifications */
  enableSound?: boolean;
  /** Enable browser notifications */
  enableBrowserNotifications?: boolean;
  /** Auto-request browser notification permission */
  requestPermission?: boolean;
  /** Filter criteria */
  filter?: {
    unreadOnly?: boolean;
    categories?: string[];
    priorities?: string[];
    since?: DateInput;
  };
  /** Sort order */
  sortBy?: 'priority' | 'date' | 'category';
  /** Maximum number of notifications to return */
  limit?: number;
}

export interface UseNotificationsReturn {
  // Notification data
  notifications: NotificationItem[];
  unreadCount: number;
  totalCount: number;
  hasUnread: boolean;
  summary: ReturnType<typeof NotificationUtils.createNotificationSummary>;

  // Connection status
  connectionStatus: string;
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;

  // Actions
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  removeNotification: (notificationId: string) => void;
  /** Retire an answered invitation — a removal with no "deleted" toast. */
  resolveNotification: (notificationId: string) => void;
  clearNotifications: () => void;
  clearOldNotifications: (maxAge?: number) => void;

  // Utilities
  formatTitle: (notification: NotificationItem) => string;
  formatMessage: (notification: NotificationItem) => string;
  formatTime: (notification: NotificationItem) => string;
  getActionUrl: (notification: NotificationItem) => string | null;
  isNotificationActionable: (notification: NotificationItem) => boolean;

  // Preferences
  preferences: NotificationPreferences;
  updatePreferences: (prefs: Partial<NotificationPreferences>) => void;

  // Loading states
  isLoading: boolean;
}

// =============================================================================
// MAIN HOOK
// =============================================================================

/**
 * Arrivals already announced. Module-level because this hook is mounted by
 * more than one component at once (the header bell, the inbox page), and an
 * arrival is announced once, not once per consumer.
 */
const announcedNotificationIds = new Set<string>();

/**
 * The OS popups still open. `Notification` has no "close all", so the hook
 * keeps a reference to each one it raises and closes them at the session
 * boundary — on the way out only: they are the account's that is leaving, and
 * a click on one navigates inside whoever is signed in by then.
 */
const openBrowserNotifications = new Set<Notification>();

registerSessionScoped(
  'browserNotifications',
  ({ previous }) => {
    if (previous === null) return;
    for (const popup of openBrowserNotifications) popup.close();
    openBrowserNotifications.clear();
  },
  { scope: 'account' }
);

export const useNotifications = (
  options: UseNotificationsOptions = {}
): UseNotificationsReturn => {
  const {
    enableSound = true,
    enableBrowserNotifications = true,
    requestPermission = true,
    filter,
    limit = 50,
  } = options;

  // Get Echo context for connection status
  const { isConnected, subscribeToNotifications } = useEchoContext();

  // Check if notifications are enabled via kill switch
  const notificationsEnabled = NOTIFICATION_CONSTANTS.LIVE_NOTIFICATIONS;

  // Pull only the fields we need from the store.
  // Avoid selectors that return fresh objects each time (can trigger
  // React "getSnapshot should be cached" + infinite update loops).
  const allNotifications = useNotificationStore((s) => s.notifications);
  const unreadCountStore = useNotificationStore((s) => s.unreadCount);
  const totalCountStore = useNotificationStore((s) => s.totalCount);

  const connectionStatus = useNotificationStore((s) => s.connectionStatus);
  const isConnectingStore = useNotificationStore((s) => s.isConnecting);
  const connectionErrorStore = useNotificationStore((s) => s.connectionError);

  const maxNotifications = useNotificationStore((s) => s.maxNotifications);
  const autoMarkAsRead = useNotificationStore((s) => s.autoMarkAsRead);
  const soundEnabled = useNotificationStore((s) => s.soundEnabled);

  const storeClearNotifications = useNotificationStore(
    (s) => s.clearNotifications
  );
  const storeClearOldNotifications = useNotificationStore(
    (s) => s.clearOldNotifications
  );

  // Read/unread/delete go through the shared write path so they persist to the
  // server and stay consistent with the inbox page. The dropdown/bell are
  // fire-and-forget, so swallow the rejection here (the action already shows a
  // toast and rolls the store back on failure).
  const actions = useNotificationActions();

  const markAsRead = useCallback(
    (notificationId: string): void => {
      void actions.markAsRead(notificationId).catch(() => undefined);
    },
    [actions]
  );

  const markAllAsRead = useCallback((): void => {
    void actions.markAllAsRead().catch(() => undefined);
  }, [actions]);

  const removeNotification = useCallback(
    (notificationId: string): void => {
      void actions.deleteNotification(notificationId).catch(() => undefined);
    },
    [actions]
  );

  const resolveNotification = useCallback(
    (notificationId: string): void => {
      void actions.resolveNotification(notificationId);
    },
    [actions]
  );

  const setMaxNotifications = useNotificationStore(
    (s) => s.setMaxNotifications
  );
  const setAutoMarkAsRead = useNotificationStore((s) => s.setAutoMarkAsRead);
  const setSoundEnabled = useNotificationStore((s) => s.setSoundEnabled);

  // Local state for browser notifications
  const [browserPermission, setBrowserPermission] =
    useState<NotificationPermission>('default');
  const [isLoading] = useState(false);

  // ==========================================================================
  // BROWSER NOTIFICATION PERMISSIONS
  // ==========================================================================

  useEffect(() => {
    if ('Notification' in window) {
      setBrowserPermission(Notification.permission);

      if (
        requestPermission &&
        enableBrowserNotifications &&
        Notification.permission === 'default'
      ) {
        Notification.requestPermission().then((permission) => {
          setBrowserPermission(permission);
        });
      }
    }
  }, [enableBrowserNotifications, requestPermission]);

  // ==========================================================================
  // NOTIFICATION PROCESSING
  // ==========================================================================

  // Process and filter notifications
  const processedNotifications = useMemo(() => {
    let filtered = allNotifications;

    // Apply filters
    if (filter) {
      filtered = NotificationUtils.filterNotifications(filtered, filter);
    }

    // Sort notifications
    filtered = NotificationUtils.sortNotifications(filtered);

    // Apply limit
    if (limit && limit > 0) {
      filtered = filtered.slice(0, limit);
    }

    return filtered;
  }, [allNotifications, filter, limit]);

  // Create summary
  const summary = useMemo(() => {
    return NotificationUtils.createNotificationSummary(processedNotifications);
  }, [processedNotifications]);

  // ==========================================================================
  // SOUND AND BROWSER NOTIFICATIONS
  // ==========================================================================

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (!enableSound || !soundEnabled) return;

    try {
      // Create a simple beep sound using Web Audio API
      const audioContext = new (
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext
      )();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        audioContext.currentTime + 0.5
      );

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.warn(
        '[useNotifications] Failed to play notification sound:',
        error
      );
    }
  }, [enableSound, soundEnabled]);

  // Show browser notification
  const showBrowserNotification = useCallback(
    (notification: NotificationItem) => {
      if (!enableBrowserNotifications || browserPermission !== 'granted')
        return;
      if (document.visibilityState === 'visible') return; // Don't show if app is visible

      try {
        const title = NotificationUtils.formatNotificationTitle(notification);
        const message =
          NotificationUtils.formatNotificationMessage(notification);
        const priority = NotificationUtils.getPriority(notification);

        const browserNotification = new Notification(title, {
          body: message,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: notification.id,
          requireInteraction: priority === 'high' || priority === 'error',
          silent: false,
        });
        openBrowserNotifications.add(browserNotification);
        browserNotification.onclose = () => {
          openBrowserNotifications.delete(browserNotification);
        };

        // Handle click
        browserNotification.onclick = () => {
          window.focus();
          browserNotification.close();

          const actionUrl =
            NotificationUtils.getNotificationAction(notification);
          if (actionUrl && actionUrl.startsWith('/')) {
            // Navigate to internal route
            window.location.href = actionUrl;
          }
        };

        // Auto-close after 5 seconds for low priority notifications
        if (priority === 'low') {
          setTimeout(() => {
            browserNotification.close();
          }, 5000);
        }
      } catch (error) {
        console.warn(
          '[useNotifications] Failed to show browser notification:',
          error
        );
      }
    },
    [enableBrowserNotifications, browserPermission]
  );

  // ==========================================================================
  // NOTIFICATION EVENT HANDLERS
  // ==========================================================================

  // Announce each LIVE arrival — a sound, and an OS notification when the app
  // is off screen. Driven by the socket rather than by the store changing:
  // watching the store announced every unread notification again on each
  // change, the ones loaded over REST included.
  useEffect(
    () =>
      subscribeToNotifications((notification) => {
        if (announcedNotificationIds.has(notification.id)) return;
        announcedNotificationIds.add(notification.id);

        playNotificationSound();
        showBrowserNotification(notification);
      }),
    [subscribeToNotifications, playNotificationSound, showBrowserNotification]
  );

  // ==========================================================================
  // UTILITY FUNCTIONS
  // ==========================================================================

  const formatTitle = useCallback((notification: NotificationItem): string => {
    return NotificationUtils.formatNotificationTitle(notification);
  }, []);

  const formatMessage = useCallback(
    (notification: NotificationItem): string => {
      return NotificationUtils.formatNotificationMessage(notification);
    },
    []
  );

  const formatTime = useCallback((notification: NotificationItem): string => {
    return formatDisplayDate(notification.created_at, 'relativeShort') ?? '';
  }, []);

  const getActionUrl = useCallback(
    (notification: NotificationItem): string | null => {
      return NotificationUtils.getNotificationAction(notification);
    },
    []
  );

  const isNotificationActionable = useCallback(
    (notification: NotificationItem): boolean => {
      return NotificationUtils.isActionable(notification);
    },
    []
  );

  // ==========================================================================
  // PREFERENCE MANAGEMENT
  // ==========================================================================

  const preferences = useMemo<NotificationPreferences>(
    () => ({
      soundEnabled,
      browserNotificationsEnabled: browserPermission === 'granted',
      autoMarkAsRead,
      maxNotifications,
    }),
    [soundEnabled, browserPermission, autoMarkAsRead, maxNotifications]
  );

  const updatePreferences = useCallback(
    (prefs: Partial<NotificationPreferences>) => {
      if (prefs.soundEnabled !== undefined) {
        setSoundEnabled(prefs.soundEnabled);
      }
      if (prefs.autoMarkAsRead !== undefined) {
        setAutoMarkAsRead(prefs.autoMarkAsRead);
      }
      if (prefs.maxNotifications !== undefined) {
        setMaxNotifications(prefs.maxNotifications);
      }
      if (
        prefs.browserNotificationsEnabled !== undefined &&
        'Notification' in window
      ) {
        if (
          prefs.browserNotificationsEnabled &&
          Notification.permission !== 'granted'
        ) {
          Notification.requestPermission().then(setBrowserPermission);
        }
      }
    },
    [setSoundEnabled, setAutoMarkAsRead, setMaxNotifications]
  );

  // ==========================================================================
  // RETURN INTERFACE
  // ==========================================================================

  return {
    // Notification data
    notifications: notificationsEnabled ? processedNotifications : [],
    unreadCount: notificationsEnabled ? unreadCountStore : 0,
    totalCount: notificationsEnabled ? totalCountStore : 0,
    hasUnread: notificationsEnabled ? unreadCountStore > 0 : false,
    summary: notificationsEnabled
      ? summary
      : {
          total: 0,
          unread: 0,
          categories: {},
          priorities: { high: 0, medium: 0, low: 0, error: 0 },
          hasErrors: false,
          hasHighPriority: false,
        },

    // Connection status
    connectionStatus: notificationsEnabled ? connectionStatus : 'disconnected',
    isConnected: notificationsEnabled && isConnected,
    isConnecting: notificationsEnabled && isConnectingStore,
    connectionError: notificationsEnabled
      ? connectionErrorStore
      : 'Notifications disabled',

    // Actions
    markAsRead,
    markAllAsRead,
    removeNotification,
    resolveNotification,
    clearNotifications: storeClearNotifications,
    clearOldNotifications: storeClearOldNotifications,

    // Utilities
    formatTitle,
    formatMessage,
    formatTime,
    getActionUrl,
    isNotificationActionable,

    // Preferences
    preferences,
    updatePreferences,

    // Loading states
    isLoading,
  };
};

// =============================================================================
// SPECIALIZED HOOKS
// =============================================================================

/**
 * Hook for getting unread notifications only
 */
export const useUnreadNotifications = () => {
  return useNotifications({
    filter: { unreadOnly: true },
    sortBy: 'priority',
  });
};

/**
 * Hook for getting notifications by category
 */
export const useNotificationsByCategory = (category: string) => {
  return useNotifications({
    filter: { categories: [category] },
    sortBy: 'date',
  });
};

/**
 * Hook for getting high priority notifications
 */
export const useHighPriorityNotifications = () => {
  return useNotifications({
    filter: { priorities: ['high', 'error'] },
    sortBy: 'priority',
    limit: 10,
  });
};

/**
 * Hook for notification counts only (lightweight)
 */
export const useNotificationCounts = () => {
  const unread = useNotificationStore((s) => s.unreadCount);
  const total = useNotificationStore((s) => s.totalCount);
  const hasUnread = unread > 0;
  const { isConnected } = useEchoContext();

  return {
    unreadCount: unread,
    totalCount: total,
    hasUnread,
    isConnected,
  };
};

/**
 * Hook for notification connection status only
 */
export const useNotificationConnectionStatus = () => {
  const status = useNotificationStore((s) => s.connectionStatus);
  const isConnecting = useNotificationStore((s) => s.isConnecting);
  const isConnected = useNotificationStore((s) => s.isConnected);
  const error = useNotificationStore((s) => s.connectionError);
  const lastConnectedAt = useNotificationStore((s) => s.lastConnectedAt);
  const reconnectAttempts = useNotificationStore((s) => s.reconnectAttempts);

  return {
    status,
    isConnecting,
    isConnected,
    error,
    lastConnectedAt,
    reconnectAttempts,
  };
};

export default useNotifications;
